import { useCallback, useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { OGLRenderingContext } from 'ogl';

import { PRISM_MOTION_VALUE_CHANGE_EVENT } from './prismConstants';
import { mountPrismRenderer } from './prismMount';
import { isMotionValue, readNumber } from './prismMotionValue';
import type { PrismPlaybackControls } from './prismRenderLoop';
import { calculatePrismPxScale } from './prismResize';
import { shouldPausePrism } from './prismVisibility';
import type { DynamicNumber, PrismMutableRef, PrismOffset, PrismProgram, PrismProps } from './prismTypes';

interface UsePrismRendererInput extends Required<Omit<PrismProps, 'onError' | 'onFpsSample'>> {
  onError?: (error: unknown) => void;
  onFpsSample?: PrismProps['onFpsSample'];
}

export function usePrismRenderer({
  height,
  baseWidth,
  animationType,
  glow,
  offset,
  noise,
  transparent,
  scale,
  hueShift,
  colorFrequency,
  hoverStrength,
  inertia,
  bloom,
  suspendWhenOffscreen,
  timeScale,
  maxDpr,
  paused,
  onError,
  onFpsSample,
}: UsePrismRendererInput): RefObject<HTMLDivElement | null> {
  const onFpsSampleRef = useRef(onFpsSample);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const programRef = useRef<PrismProgram | null>(null);
  const glRef = useRef<OGLRenderingContext | null>(null);
  const glowRef = useRef<number>(readNumber(glow));
  const scaleRef = useRef<number>(readNumber(scale));
  const offsetX = getPrismOffsetX(offset);
  const offsetY = getPrismOffsetY(offset);
  const playbackRef = useRef<PrismPlaybackControls | null>(null);
  const pausedRef = useRef(paused);

  useEffect(() => {
    onFpsSampleRef.current = onFpsSample;
  }, [onFpsSample]);

  // Declared before the scene-mount effect so a config-driven remount in this same
  // commit seeds itself with this commit's coverage state, while `paused` stays out
  // of the mount dependencies and never destroys the WebGL context on its own.
  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const mounted = mountPrismRenderer({
      container,
      height,
      baseWidth,
      animationType,
      noise,
      offsetX,
      offsetY,
      transparent,
      hueShift,
      colorFrequency,
      hoverStrength,
      inertia,
      bloom,
      suspendWhenOffscreen,
      timeScale,
      maxDpr,
      onError,
      glowRef,
      scaleRef,
      programRef,
      glRef,
      initiallyPaused: shouldPausePrism({
        documentHidden: document.hidden,
        externallyPaused: pausedRef.current,
      }),
      onFpsSampleRef,
    });

    playbackRef.current = mounted?.playback ?? null;
    return (): void => {
      playbackRef.current = null;
      mounted?.cleanup();
    };
  }, [
    height,
    baseWidth,
    animationType,
    noise,
    offsetX,
    offsetY,
    transparent,
    hueShift,
    colorFrequency,
    timeScale,
    hoverStrength,
    inertia,
    bloom,
    suspendWhenOffscreen,
    maxDpr,
    onError,
  ]);

  useEffect(() => {
    const syncPlayback = (): void => {
      const covered = shouldPausePrism({ documentHidden: document.hidden, externallyPaused: paused });
      if (covered) playbackRef.current?.pause();
      else playbackRef.current?.resume();
    };

    syncPlayback();
    document.addEventListener('visibilitychange', syncPlayback);
    return (): void => {
      document.removeEventListener('visibilitychange', syncPlayback);
    };
  }, [paused]);

  const applyGlow = useCallback((latest: number): void => {
    applyPrismGlow({ latest, glowRef, programRef });
  }, []);

  const applyScale = useCallback((latest: number): void => {
    applyPrismScale({ latest, scaleRef, programRef, glRef });
  }, []);

  usePrismDynamicNumber({ value: glow, applyValue: applyGlow });
  usePrismDynamicNumber({ value: scale, applyValue: applyScale });

  return containerRef;
}

interface UsePrismDynamicNumberInput {
  value: DynamicNumber;
  applyValue: (latest: number) => void;
}

function usePrismDynamicNumber({ value, applyValue }: UsePrismDynamicNumberInput): void {
  useEffect(() => {
    if (isMotionValue(value)) {
      applyValue(value.get());
      return value.on(PRISM_MOTION_VALUE_CHANGE_EVENT, applyValue);
    }

    applyValue(value);
  }, [value, applyValue]);
}

function getPrismOffsetX(offset: PrismOffset): number {
  return offset.x ?? 0;
}

function getPrismOffsetY(offset: PrismOffset): number {
  return offset.y ?? 0;
}

interface ApplyPrismGlowInput {
  latest: number;
  glowRef: PrismMutableRef<number>;
  programRef: PrismMutableRef<PrismProgram | null>;
}

function applyPrismGlow({ latest, glowRef, programRef }: ApplyPrismGlowInput): void {
  glowRef.current = latest;
  const program = programRef.current;
  if (program) program.uniforms.uGlow.value = Math.max(0.0, latest);
}

interface ApplyPrismScaleInput {
  latest: number;
  scaleRef: PrismMutableRef<number>;
  programRef: PrismMutableRef<PrismProgram | null>;
  glRef: PrismMutableRef<OGLRenderingContext | null>;
}

function applyPrismScale({ latest, scaleRef, programRef, glRef }: ApplyPrismScaleInput): void {
  scaleRef.current = latest;
  const program = programRef.current;
  const gl = glRef.current;
  if (!program) return;
  if (!gl) return;

  const nextScale = Math.max(0.001, latest);
  program.uniforms.uScale.value = nextScale;
  program.uniforms.uPxScale.value = calculatePrismPxScale({
    drawingBufferHeight: gl.drawingBufferHeight,
    scale: nextScale,
  });
}
